import { useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LogOut, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { moreMenuGroups } from './nav-config';
import { ExtraNavBadges, extraBadgeLabel } from './ExtraNavBadges';
import { useAuth } from '../../hooks/useAuth';
import { useBillingFeatures } from '../../hooks/useBilling';
import { useIsProductionEnvironment } from '../../hooks/useSystemEnvironment';

interface MoreMenuProps {
  open: boolean;
  onClose: () => void;
  badges?: Record<string, number>;
}

export function MoreMenu({ open, onClose, badges = {} }: MoreMenuProps) {
  const { manager, logout } = useAuth();
  const navigate = useNavigate();
  const { data: featuresData } = useBillingFeatures();
  const isProduction = useIsProductionEnvironment();
  // Radix only returns focus to a Dialog.Trigger; the More tab lives in BottomTabBar, so the
  // element focused when the sheet opened (the More tab) is captured and refocused on close.
  const openerRef = useRef<HTMLElement | null>(null);

  const handleLogout = async () => {
    onClose();
    await logout();
    navigate('/login');
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="bottom"
        data-testid="more-menu"
        className="flex h-[85dvh] max-h-[85dvh] flex-col gap-0 rounded-t-xl p-0 [&>button:first-child]:hidden"
        onOpenAutoFocus={() => {
          openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          openerRef.current?.focus();
        }}
      >
        <SheetHeader className="shrink-0 border-b border-border px-6 pb-4 pt-6">
          <div className="flex items-center justify-between gap-3">
            <SheetTitle className="min-w-0 truncate text-left text-base font-semibold">
              {manager?.email ?? 'Menu'}
            </SheetTitle>
            <SheetDescription className="sr-only">
              Additional navigation links and account actions
            </SheetDescription>
            <button
              type="button"
              onClick={onClose}
              data-testid="more-menu-close"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md hover:bg-accent transition-colors"
              aria-label="Close menu"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </SheetHeader>

        <nav
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))]"
          aria-label="More navigation"
          data-testid="more-menu-nav"
        >
          {moreMenuGroups.map((group) => (
            <div key={group.label} className="py-2">
              <h3 className="px-6 mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group.label}
              </h3>
              {group.items.filter((item) =>
                (!item.featureKey || !featuresData || featuresData.features.includes(item.featureKey)) &&
                !(item.hiddenInProduction && isProduction),
              ).map((item) => {
                const Icon = item.icon;
                const badge = item.badgeKey ? badges[item.badgeKey] : undefined;

                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    aria-label={
                      (badge != null && badge > 0) || extraBadgeLabel(item, badges)
                        ? item.label + (badge != null && badge > 0 ? `, ${badge} ${item.badgeUnit ?? 'unread'}` : '') + extraBadgeLabel(item, badges)
                        : undefined
                    }
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-11 items-center gap-3 px-6 py-2.5 text-sm transition-colors',
                        isActive
                          ? 'text-primary bg-accent'
                          : 'text-foreground hover:bg-accent',
                      )
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {badge != null && badge > 0 && (
                      <span
                        className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground"
                        aria-hidden="true"
                      >
                        {badge}
                      </span>
                    )}
                    <ExtraNavBadges item={item} badges={badges} />
                  </NavLink>
                );
              })}
            </div>
          ))}

          {/* Sign Out */}
          <div className="border-t border-border mt-2 pt-2">
            <button
              type="button"
              onClick={handleLogout}
              className="flex min-h-11 w-full items-center gap-3 px-6 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span>Sign Out</span>
            </button>
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
